import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

export interface SpinnerState {
  isLoading: boolean;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SpinnerService {
  private spinnerSubject = new BehaviorSubject<SpinnerState>({ isLoading: false });
  private activeRequestCount = 0;
  spinnerState$ = this.spinnerSubject.asObservable();

  constructor() {
  }

  /**
   * This function is used to show the spinner
   * @param message Optional message to display with the spinner
   */
  show(message?: string) {
    this.activeRequestCount++;
    this.spinnerSubject.next({
      isLoading: true,
      message: message ?? this.spinnerSubject.value.message
    });
  }
  
  /**
   * This function is used to hide the spinner
   */
  hide() {
    if (this.activeRequestCount > 0) {
      this.activeRequestCount--;
    }

    if (this.activeRequestCount === 0) {
      this.spinnerSubject.next({ isLoading: false });
    }
  }

  /**
   * Utility method to wrap an Observable with automatic spinner management
   * This is the recommended approach for API calls as it automatically handles
   * showing and hiding the spinner regardless of success or error.
   * 
   * @param observable The Observable to wrap
   * @param message Optional message to show with the spinner
   * @returns Observable that automatically shows/hides spinner
   * 
   * @example
   * // Basic usage
   * this._spinnerService.withSpinner(
   *   this._httpService.getData(),
   *   'Loading data...'
   * ).subscribe(data => {
   *   // Handle data
   * });
   * 
   * // With error handling
   * this._spinnerService.withSpinner(
   *   this._httpService.getData(),
   *   'Loading data...'
   * ).subscribe({
   *   next: (data) => {
   *     // Handle success
   *   },
   *   error: (error) => {
   *     // Handle error - spinner is automatically hidden
   *   }
   * });
   */
  withSpinner<T>(observable: Observable<T>, message?: string): Observable<T> {
    this.show(message);
    return observable.pipe(
      finalize(() => this.hide())
    );
  }
}
